import React, { useCallback, useState } from "react";
import { IMediaMusicWithMeta } from "@/store/models/music.models";
import { Pagination, Skeleton } from "@mui/material";
import EditMusicModal from "@/pages/MusicPage/components/EditMusicModal/EditMusicModal";
import DeleteModal from "@/components/DeleteModal/DeleteModal";
import styles from "./LikedMusic.module.scss";
import { Profile } from "@/store/models/users.models";
import { AudioCard } from "@/components/AudioCard/AudioCard";
import useItemsPerPage from "@/hooks/useItemsPerPage";
import {
  useDeleteMediaMutation,
  useGetMediaMusicQuery,
  useGetWatchedMediaQuery,
  useUpdateMediaMutation,
} from "@/store/rtk-api/media.api";
import { IMedia, IWatchedMediaWithMeta } from "@/store/models/video.models";

type Props = {
  profile: Profile;
  type: "liked" | "watched";
};

const LikedMusic = ({ profile, type }: Props) => {
  const [page, setPage] = useState(1);
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [selectedMusic, setSelectedMusic] = useState<IMedia | null>(null);
  const itemsPerPage = useItemsPerPage(2, 4, 6, 1164, 1680);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const {
    data: likedMusicData,
    isLoading: isLikedLoading,
    isSuccess: isLikedSuccess,
  } = useGetMediaMusicQuery({
    page: page,
    limit: itemsPerPage,
    profileUid: profile.uid,
    isFavourite: true,
  });

  const {
    data: watchedMusicData,
    isLoading: isWatchedLoading,
    isSuccess: isWatchedSuccess,
  } = useGetWatchedMediaQuery({
    page: 1,
    limit: 10,
    profileUid: profile.uid,
    mediaType: "audio",
  });

  const isLoading = type === "liked" ? isLikedLoading : isWatchedLoading;
  const isSuccess = type === "liked" ? isLikedSuccess : isWatchedSuccess;
  const musicData: IMediaMusicWithMeta | IWatchedMediaWithMeta | undefined =
    type === "liked" ? likedMusicData : watchedMusicData;

  const [deleteMusicByUid] = useDeleteMediaMutation();
  const [editMusic] = useUpdateMediaMutation();

  const handleToggleEditModal = (music: IMedia) => {
    setSelectedMusic(music);
    setIsOpenEditModal(true);
  };

  const handleToggleDeleteModal = (music: IMedia) => {
    setSelectedMusic(music);
    setIsOpenDeleteModal(true);
  };

  const handlEditMusic = useCallback(async () => {
    if (selectedMusic) {
      await editMusic({
        uid: selectedMusic.uid,
        title: selectedMusic.title,
        description: selectedMusic.description,
        subtitleUrl: selectedMusic.subtitleUrl,
        thumbnailFile: selectedMusic.thumbnailFile as File,
        mediaSrcFile: selectedMusic.mediaSrcFile as File,
      });
      setIsOpenEditModal(false);
    }
  }, [editMusic, selectedMusic]);

  const handleDeleteMusic = useCallback(async () => {
    if (selectedMusic) {
      await deleteMusicByUid(selectedMusic.uid);
      setIsOpenDeleteModal(false);
    }
  }, [selectedMusic, deleteMusicByUid]);

  const musicList =
    type === "liked"
      ? (musicData as IMediaMusicWithMeta)?.media
      : (musicData as IWatchedMediaWithMeta)?.data;

  return (
    <div className={styles.container}>
      <div className={styles.itemsWithPagination}>
        <div className={styles.itemsContainer}>
          {isLoading &&
            Array(4)
              .fill(0)
              .map((_, index) => (
                <div className={styles.item} key={index}>
                  <div className={styles.itemInfoWrap}>
                    <Skeleton
                      variant="rectangular"
                      width={150}
                      height={150}
                      animation="wave"
                      sx={{ borderRadius: "8px" }}
                    />
                    <div className={styles.itemInfo}>
                      <Skeleton
                        variant="text"
                        width={100}
                        animation="wave"
                        sx={{ marginBottom: 1 }}
                      />
                      <Skeleton
                        variant="text"
                        width={150}
                        animation="wave"
                        sx={{ marginBottom: 1 }}
                      />
                    </div>
                  </div>
                  <div className={styles.itemBtns}>
                    <Skeleton
                      variant="circular"
                      width={40}
                      height={40}
                      animation="wave"
                      sx={{ marginRight: 1 }}
                    />
                    <Skeleton
                      variant="circular"
                      width={40}
                      height={40}
                      animation="wave"
                    />
                  </div>
                </div>
              ))}
          {isSuccess && musicList && musicList.length === 0 && (
            <div className={styles.noDataText}>
              {type === "liked"
                ? "There is no data about favorite songs"
                : "There is no data about watched songs"}
            </div>
          )}
          {isSuccess &&
            musicList &&
            musicList.map((audio) => (
              <AudioCard
                key={audio.uid}
                audio={audio}
                handleToggleEditModal={handleToggleEditModal}
                handleToggleDeleteModal={handleToggleDeleteModal}
                isEdit={true}
              />
            ))}
        </div>
        {isOpenEditModal && (
          <EditMusicModal
            isOpenEditModal={isOpenEditModal}
            setIsOpenEditModal={setIsOpenEditModal}
            selectedMusic={selectedMusic}
            setSelectedMusic={setSelectedMusic}
            handleEditMusic={handlEditMusic}
          />
        )}
        {isOpenDeleteModal && (
          <DeleteModal
            isOpen={isOpenDeleteModal}
            setIsOpen={setIsOpenDeleteModal}
            name={selectedMusic ? selectedMusic?.title : ""}
            supportingText="Are you sure you want to delete this audio?"
            onDelete={handleDeleteMusic}
          />
        )}
        {isSuccess && musicData && musicData.total > 0 && (
          <Pagination
            page={page}
            onChange={handlePageChange}
            count={Math.ceil(musicData.total / itemsPerPage)}
          />
        )}
      </div>
    </div>
  );
};

export default LikedMusic;

